// ============================================
// Ad-Hoc Dose Log Modal
// ============================================
// For users who have a vial but no protocol attached. Lets them log a
// dose-of-the-moment against the vial without setting up a structured
// schedule first. Mirrors DoseLogModal's UX and reuses its CSS, but
// drops all the protocol/phase/component plumbing — vials are the
// source of truth here.
//
// On save: writes a dose_logs row with protocolId/componentId/phaseId
// all null, inventoryId set, then runs the log_dose_with_inventory RPC.
// The RPC auto-backfills inventory.total_doses on the FIRST ad-hoc dose
// against a vial that was created without a planned dose (Phase 1 of
// the 2026-04-29 ad-hoc work). For researching vials, save also flips
// is_researching to false — logging a dose IS starting the vial.
//
// Backfill mode: when defaultDate is in the past, the modal pre-fills
// the date/time inputs to that date so users can log past doses.

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input, DosingDisclaimer } from '../../components/Components';
import { DrawChangeModal } from '../../components/DrawChangeModal';
import { InjectionSite, INJECTION_SITE_LABELS } from '../../data/types';
import type { InventoryItem, DoseLog } from '../../data/types';
import {
  logDoseWithInventory,
  maybeAutoSwitchEmptyVial,
  updateInventoryItem,
  fetchLastDoseForPeptide,
  fetchProtocols,
} from '../../data/cloud';
import { useCloudQuery } from '../../data/hooks';
import { scheduledProtocolHoldingVial } from '../../utils/protocolActivation';
import { parseLocalDate } from '../../utils/schedule';
import { formatUnits, unitsForDose } from '../../utils/calculations';
import { useToast } from '../../components/Toast';
import { hapticNotification } from '../../utils/native';
import { uuid } from '../../utils/uuid';
import './DoseLogModal.css';

interface AdHocDoseLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The single vial to log against. When present, modal goes straight to
   * the dose-entry form. When null and `vials` is non-empty, the modal
   * shows a picker first.
   */
  vial: InventoryItem | null;
  /**
   * Optional list of in-use vials. Surfaces a picker when more than one
   * exists and no `vial` is pre-selected. Used by the global FAB on
   * Layout when the user has multiple vials but no active protocol.
   */
  vials?: InventoryItem[];
  /** Pre-filled date for backfill mode (logging a past dose). Defaults to now. */
  defaultDate?: Date;
  onSaved?: () => void;
}

type DoseUnit = 'mcg' | 'mg';

export const AdHocDoseLogModal: React.FC<AdHocDoseLogModalProps> = ({
  isOpen,
  onClose,
  vial,
  vials,
  defaultDate,
  onSaved,
}) => {
  const { showToast } = useToast();
  const [pickedVial, setPickedVial] = useState<InventoryItem | null>(null);
  const [doseValue, setDoseValue] = useState('');
  const [doseUnit, setDoseUnit] = useState<DoseUnit>('mcg');
  const [selectedSite, setSelectedSite] = useState<InjectionSite>(InjectionSite.ABDOMEN_LEFT);
  const [notes, setNotes] = useState('');
  const [logDate, setLogDate] = useState('');
  const [logTime, setLogTime] = useState('');
  const [saving, setSaving] = useState(false);
  // Auto-promoted backup changed the draw - shows the "I understand" modal.
  const [promoteDrawChange, setPromoteDrawChange] = useState<{ oldUnits: number; newUnits: number; peptideName: string } | null>(null);
  const savingRef = useRef(false);

  // Early-start guard (scheduled protocols): a vial reserved by a scheduled
  // protocol is not an ad-hoc target while it waits - logging against it
  // would flip a researching vial without a real mix and dose supply the
  // protocol still owns. The protocol path (activation) is the way in.
  const { data: protocols } = useCloudQuery('protocols', fetchProtocols, []);

  // Active vial: prefer the prop, fall back to picker selection.
  const activeVial = vial ?? pickedVial;
  const heldBy = activeVial ? scheduledProtocolHoldingVial(activeVial.id, protocols) : null;
  // Show picker when no vial is pre-selected AND we have a list to pick from.
  const pickableVials = (vials ?? []).filter((v) => !scheduledProtocolHoldingVial(v.id, protocols));
  const showPicker = !activeVial && pickableVials.length > 0;

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setPickedVial(null);
    setDoseValue('');
    setDoseUnit('mcg');
    setNotes('');
    const d = defaultDate ?? new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setLogDate(`${y}-${m}-${day}`);
    setLogTime(d.toTimeString().slice(0, 5));
  }, [isOpen, defaultDate]);

  // Auto-rotate to the next site after the user's last log for this peptide.
  // Re-runs when the active vial changes (picker → form transition).
  useEffect(() => {
    if (!isOpen || !activeVial) return;
    let cancelled = false;
    fetchLastDoseForPeptide(activeVial.peptideId).then((lastLog) => {
      if (cancelled || !lastLog) return;
      const lastSite = lastLog.injectionSite as InjectionSite;
      const siteOrder = Object.values(InjectionSite);
      const lastIdx = siteOrder.indexOf(lastSite);
      if (lastIdx >= 0) {
        setSelectedSite(siteOrder[(lastIdx + 1) % siteOrder.length]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeVial]);

  // Convert user input to mcg using a uniform unit conversion. mg → mcg
  // is 1000x. mL is intentionally NOT supported here — for ad-hoc, users
  // think in mass (mg/mcg), and the modal already knows the vial's
  // concentration so we don't need users entering volumes.
  const doseRaw = parseFloat(doseValue) || 0;
  const doseMcg = doseUnit === 'mg' ? doseRaw * 1000 : doseRaw;
  const computedUnits = activeVial
    ? unitsForDose(doseMcg, activeVial.concentrationMcgPerUnit)
    : 0;
  const canSave = !!activeVial && !heldBy && doseMcg > 0 && computedUnits > 0 && !saving;

  const handleSave = async () => {
    if (!canSave || !activeVial) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const loggedAt = new Date(`${logDate}T${logTime}:00`).toISOString();
      const now = new Date().toISOString();

      const doseLog: DoseLog = {
        id: uuid(),
        protocolId: null,
        componentId: null,
        phaseId: null,
        inventoryId: activeVial.id,
        peptideId: activeVial.peptideId,
        peptideName: activeVial.peptideName,
        doseMcg,
        units: computedUnits,
        injectionSite: selectedSite,
        loggedTime: loggedAt,
        skipped: false,
        notes: notes.trim() || undefined,
        createdAt: now,
      };

      // Treat each ad-hoc dose as one inventory dose. The RPC auto-
      // backfills total_doses when this is the first dose against a
      // vial created without a planned dose.
      const result = await logDoseWithInventory(doseLog, activeVial.id, 1);

      // If the vial was researching, logging a dose starts it.
      if (activeVial.isResearching) {
        await updateInventoryItem(activeVial.id, { isResearching: false });
      }

      hapticNotification('success');
      showToast('Dose logged');
      onSaved?.();

      // Empty-vial prompt: result is the post-decrement remaining count.
      // Null when the vial had no total_doses yet AND the RPC didn't
      // back-fill (shouldn't happen with our RPC, but defensive).
      if (result.inventoryDosesRemaining !== null && result.inventoryDosesRemaining <= 0) {
        const outcome = await maybeAutoSwitchEmptyVial(activeVial.id);
        if (outcome.kind === 'promoted') {
          if (outcome.drawChange) {
            // Backup mixed differently means a different draw for the same
            // dose. The summary refresh already fired in the data layer;
            // make the user acknowledge the new draw. Defer onClose to the
            // modal so they can't miss it.
            setPromoteDrawChange({
              oldUnits: outcome.drawChange.oldUnits,
              newUnits: outcome.drawChange.newUnits,
              peptideName: outcome.newVial.peptideName,
            });
            return;
          }
          showToast(`Switched to your backup vial of ${outcome.newVial.peptideName}`, 'success');
        } else if (outcome.kind === 'sealed_backup') {
          showToast('That looked like the last dose. Your backup vial is not mixed yet - tap "Start using" on it in your inventory when you reconstitute.');
        } else {
          showToast('That looked like the last dose - you can archive the vial from your inventory.');
        }
      }

      onClose();
    } catch (err) {
      console.error('Ad-hoc dose log failed:', err);
      showToast("Couldn't log that dose. Try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Guard state: the target vial is reserved by a SCHEDULED protocol.
  // Dosing it ad-hoc would start a vial the protocol still owns (the
  // Bug-2 reintroduction path). Shown instead of the dose form.
  if (activeVial && heldBy) {
    const startsOn = parseLocalDate(heldBy.startDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="log-modal" style={{ padding: 'var(--space-4) var(--space-4) calc(var(--space-8) + env(safe-area-inset-bottom, 24px))', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: '0 0 var(--space-2)', color: 'var(--color-text-primary)' }}>
            This vial is spoken for
          </h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}>
            It belongs to {heldBy.name}, which starts {startsOn}. You can start
            the protocol early or change its date from the protocol page.
          </p>
          <Button variant="primary" size="lg" fullWidth onClick={onClose}>OK</Button>
        </div>
      </Modal>
    );
  }

  // Empty state: no vial provided AND nothing pickable. Gate on the
  // FILTERED list - if every in-use vial is reserved by a scheduled
  // protocol, falling through to the form would crash on a null
  // activeVial (adversarial review B4).
  if (!activeVial && pickableVials.length === 0) {
    const allReserved = !!vials && vials.length > 0;
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="log-modal" style={{ padding: 'var(--space-4) var(--space-4) calc(var(--space-8) + env(safe-area-inset-bottom, 24px))', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: '0 0 var(--space-2)', color: 'var(--color-text-primary)' }}>
            {allReserved ? 'Your vials are spoken for' : 'Add a vial first'}
          </h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}>
            {allReserved
              ? 'Each of your vials belongs to a scheduled protocol. Start one early or change its date from the protocol page.'
              : 'You need at least one in-use vial before you can log a dose.'}
          </p>
          <Button variant="primary" size="lg" fullWidth onClick={onClose}>OK</Button>
        </div>
      </Modal>
    );
  }

  // Picker: when no vial pre-selected and we have a list, show vial chooser.
  if (showPicker) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="log-modal" style={{ padding: 'var(--space-2) var(--space-4) calc(var(--space-8) + env(safe-area-inset-bottom, 24px))' }}>
          <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: '0 0 var(--space-1)', color: 'var(--color-text-primary)' }}>
            Which vial?
          </h2>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}>
            Pick the vial you're logging this dose against.
          </p>
          <div className="log-picker">
            {pickableVials.map((v) => (
              <button
                key={v.id}
                type="button"
                className="log-picker-item"
                onClick={() => setPickedVial(v)}
              >
                <div className="log-picker-info">
                  <span className="log-picker-name">{v.peptideName}</span>
                  <span className="log-picker-dose">
                    {v.vialSizeMg}mg in {v.bacWaterMl}mL · {v.dosesRemaining != null && v.totalDoses != null ? `${v.dosesRemaining} of ${v.totalDoses} doses` : 'tracking after first dose'}
                  </span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="log-modal" style={{ padding: 'var(--space-2) var(--space-4) calc(var(--space-8) + env(safe-area-inset-bottom, 24px))' }}>
        <h2 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: '0 0 var(--space-1)', color: 'var(--color-text-primary)' }}>
          Log a dose
        </h2>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-4)' }}>
          {activeVial!.peptideName} · {activeVial!.vialSizeMg}mg in {activeVial!.bacWaterMl}mL BAC
        </p>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <span className="log-pill-label">Dose unit</span>
          <div className="log-pill-row" style={{ marginBottom: 'var(--space-3)' }}>
            <button
              type="button"
              className={`log-pill${doseUnit === 'mcg' ? ' log-pill--selected' : ''}`}
              onClick={() => {
                if (doseUnit === 'mcg') return;
                const n = parseFloat(doseValue);
                if (!isNaN(n) && doseValue.trim()) setDoseValue(parseFloat((n * 1000).toFixed(6)).toString());
                setDoseUnit('mcg');
              }}
            >mcg</button>
            <button
              type="button"
              className={`log-pill${doseUnit === 'mg' ? ' log-pill--selected' : ''}`}
              onClick={() => {
                if (doseUnit === 'mg') return;
                const n = parseFloat(doseValue);
                if (!isNaN(n) && doseValue.trim()) setDoseValue(parseFloat((n / 1000).toFixed(6)).toString());
                setDoseUnit('mg');
              }}
            >mg</button>
          </div>
          <Input
            label="Dose"
            type="number"
            inputMode="decimal"
            placeholder={doseUnit === 'mcg' ? 'e.g. 250' : 'e.g. 0.25'}
            suffix={doseUnit}
            value={doseValue}
            onChange={(e) => setDoseValue(e.target.value)}
            helper={
              computedUnits > 0
                ? `That's ${formatUnits(computedUnits)} units on your syringe`
                : `${activeVial!.concentrationMcgPerUnit} mcg per unit on this vial`
            }
          />
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <span className="log-pill-label">Injection site</span>
          <div className="log-pill-row">
            {Object.values(InjectionSite).map((site) => (
              <button
                key={site}
                type="button"
                className={`log-pill${selectedSite === site ? ' log-pill--selected' : ''}`}
                onClick={() => setSelectedSite(site)}
              >
                {INJECTION_SITE_LABELS[site]}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Date"
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input
              label="Time"
              type="time"
              value={logTime}
              onChange={(e) => setLogTime(e.target.value)}
            />
          </div>
        </div>

        <Input
          label="Notes (optional)"
          placeholder="Anything you want to remember about this dose"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <DosingDisclaimer variant="inline" />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
          <Button variant="primary" size="lg" fullWidth disabled={!canSave} loading={saving} onClick={handleSave}>
            Log dose
          </Button>
          <Button variant="ghost" size="md" fullWidth onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
    {promoteDrawChange && (
      <DrawChangeModal
        isOpen
        oldUnits={promoteDrawChange.oldUnits}
        newUnits={promoteDrawChange.newUnits}
        peptideName={promoteDrawChange.peptideName}
        onConfirm={() => { setPromoteDrawChange(null); onClose(); }}
      />
    )}
    </>
  );
};
